import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import useWebSocket from '../hooks/useWebSocket';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Impersonation Bootstrap — consume URL params synchronously BEFORE
  // initializing the token state, so the impersonation window picks the
  // correct token from the first render.
  const initialImpersonationActive = (() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const impToken = params.get('impersonation_token');
      if (impToken) {
        sessionStorage.setItem('imp_auth_token', impToken);
        sessionStorage.setItem('imp_target_name', params.get('impersonation_name') || '');
        sessionStorage.setItem('imp_target_email', params.get('impersonation_email') || '');
        sessionStorage.setItem('imp_admin_name', params.get('impersonation_admin') || '');
        sessionStorage.setItem('imp_active', '1');
        const url = new URL(window.location.href);
        url.searchParams.delete('impersonation_token');
        url.searchParams.delete('impersonation_name');
        url.searchParams.delete('impersonation_email');
        url.searchParams.delete('impersonation_admin');
        window.history.replaceState({}, '', url.toString());
        return true;
      }
      return sessionStorage.getItem('imp_active') === '1';
    } catch {
      return false;
    }
  })();
  const [isImpersonationWindow] = useState(initialImpersonationActive);
  // In an impersonation window we MUST use the session-scoped token, never
  // the localStorage admin token (which belongs to the original tab).
  const [token, setToken] = useState(() => {
    if (initialImpersonationActive) return sessionStorage.getItem('imp_auth_token');
    return localStorage.getItem('auth_token');
  });
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);

  // Set up axios interceptor for authentication and selected role
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Global 401 interceptor — handle expired/invalid tokens gracefully
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const url = error?.config?.url || '';
        const isAuthError = status === 401 &&
          (detail === 'Invalid authentication credentials' || detail === 'User not found');
        // Don't trigger for the /auth/login call itself (wrong password) — only for
        // subsequent authenticated requests. Also skip the /auth/me bootstrap
        // (that path already triggers logout via initAuth on failure).
        const isLoginCall = url.includes('/auth/login');
        if (isAuthError && !isLoginCall) {
          if (isImpersonationWindow) {
            // Impersonation token expired or got rejected — clear session and
            // tell the admin to close this window. Their admin tab is untouched.
            sessionStorage.removeItem('imp_auth_token');
            sessionStorage.removeItem('imp_active');
            sessionStorage.removeItem('selected_role');
            delete axios.defaults.headers.common['Authorization'];
            delete axios.defaults.headers.common['X-Selected-Role'];
            alert('Your impersonation session has expired. Please close this window and start a new one from the admin panel.');
          } else if (localStorage.getItem('auth_token')) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('selected_role');
            delete axios.defaults.headers.common['Authorization'];
            delete axios.defaults.headers.common['X-Selected-Role'];
            alert('Your session has expired. Please sign in again — your work in this tab may be lost.');
            // Give the alert time to render before reload
            setTimeout(() => { window.location.href = '/'; }, 50);
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  // Keep X-Selected-Role header in sync with selectedRole state
  useEffect(() => {
    if (selectedRole) {
      axios.defaults.headers.common['X-Selected-Role'] = selectedRole;
    } else {
      delete axios.defaults.headers.common['X-Selected-Role'];
    }
  }, [selectedRole]);

  // Load user data on app start
  useEffect(() => {
    // Helper: fetch /auth/me once with timeout.
    const fetchMeOnce = () => axios.get(`${API}/auth/me`, { timeout: 12000 });

    // Helper: classify why /auth/me failed.  A real auth failure (definitive
    // 401 from the backend) → logout.  Anything else (network blip, gateway
    // timeout, server cold-start, 5xx, etc.) → keep the user signed in and
    // retry — losing the session on a transient hiccup is what was causing
    // users on production to be repeatedly kicked out after backend pod
    // restarts.
    const isDefinitiveAuthFailure = (err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      return status === 401 && (
        detail === 'Invalid authentication credentials' ||
        detail === 'User not found'
      );
    };

    const initAuth = async () => {
      if (token) {
        // Up to 5 attempts with exponential-ish backoff (≈ 0s, 1s, 2s, 4s, 8s).
        // Total ~15s — enough to weather a typical pod restart without
        // signing the user out.
        const delays = [0, 1000, 2000, 4000, 8000];
        let success = false;
        let lastErr = null;
        for (let i = 0; i < delays.length && !success; i += 1) {
          if (delays[i]) {
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, delays[i]));
          }
          try {
            // eslint-disable-next-line no-await-in-loop
            const response = await fetchMeOnce();
            setUser(response.data);

            let resolvedRole = null;
            if (response.data.roles && response.data.roles.length === 1) {
              resolvedRole = response.data.roles[0];
            }
            const roleStore = isImpersonationWindow ? sessionStorage : localStorage;
            const savedRole = roleStore.getItem('selected_role');
            if (savedRole && response.data.roles?.includes(savedRole)) {
              resolvedRole = savedRole;
            }
            if (isImpersonationWindow && !resolvedRole && response.data.roles?.length) {
              resolvedRole = response.data.roles[0];
            }
            if (resolvedRole) {
              axios.defaults.headers.common['X-Selected-Role'] = resolvedRole;
              setSelectedRole(resolvedRole);
            }
            success = true;
          } catch (error) {
            lastErr = error;
            if (isDefinitiveAuthFailure(error)) {
              // Token actually rejected by the server — bail out immediately.
              console.warn('Auth definitively rejected — signing out.', error?.response?.data);
              logout();
              setLoading(false);
              return;
            }
            // Transient — log and retry.
            console.warn(`/auth/me attempt ${i + 1} failed (transient), will retry...`, error?.message || error);
          }
        }
        if (!success) {
          // Five attempts over ~15s all failed without a definitive 401.
          // The backend is likely down / unreachable, but the user's token
          // may still be valid.  Keep them logged in so the next attempt
          // (e.g., next route load) can succeed once the backend recovers.
          console.warn('/auth/me unreachable after retries — keeping local session, will retry on next nav.', lastErr?.message || lastErr);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token]);

  // Save selected role — sessionStorage in impersonation window (so we don't
  // pollute the admin tab), localStorage otherwise.
  useEffect(() => {
    if (selectedRole) {
      const store = isImpersonationWindow ? sessionStorage : localStorage;
      store.setItem('selected_role', selectedRole);
    }
  }, [selectedRole, isImpersonationWindow]);

  const login = async (credentials) => {
    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      const { access_token, user: userData } = response.data;
      
      // Set auth header immediately (before any re-render triggers child effects)
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      setToken(access_token);
      setUser(userData);
      localStorage.setItem('auth_token', access_token);
      
      // Set default role if user has only one role
      if (userData.roles && userData.roles.length === 1) {
        // Set header synchronously (see comment in switchRole)
        axios.defaults.headers.common['X-Selected-Role'] = userData.roles[0];
        localStorage.setItem('selected_role', userData.roles[0]);
        setSelectedRole(userData.roles[0]);
      }
      
      return userData;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      throw new Error(message);
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      throw new Error(message);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setSelectedRole(null);
    setSelectedApplication(null);
    if (isImpersonationWindow) {
      // Just clear sessionStorage for this tab
      sessionStorage.removeItem('imp_auth_token');
      sessionStorage.removeItem('imp_active');
      sessionStorage.removeItem('selected_role');
    } else {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('selected_role');
      localStorage.removeItem('impersonation');
    }
    delete axios.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['X-Selected-Role'];
  };

  const switchRole = (role) => {
    if (user && user.roles.includes(role)) {
      // Set the header SYNCHRONOUSLY before any state-change-triggered child
      // re-renders. React child effects (e.g., BrokerDashboard.fetchData on mount)
      // fire BEFORE the parent's `[selectedRole]` useEffect, which would otherwise
      // cause a stale `X-Selected-Role` header on the first fetch after switching.
      axios.defaults.headers.common['X-Selected-Role'] = role;
      localStorage.setItem('selected_role', role);
      setSelectedRole(role);
      setSelectedApplication(null);
    }
  };

  const switchApplication = (applicationId) => {
    setSelectedApplication(applicationId);
  };

  // ===========================================================================
  // Admin Impersonation — opens a NEW browser tab logged in as the target user
  // while leaving the admin's current tab untouched. Closing the new tab ends
  // the impersonation session.
  // ===========================================================================
  const impersonateUser = async (targetUserId) => {
    if (!user || !(user.roles || []).includes('admin')) {
      throw new Error('Only administrators can impersonate users');
    }
    // Ask the backend for a JWT bound to the target user
    const res = await axios.post(`${API}/admin/impersonate/${targetUserId}`);
    const newToken = res.data.access_token;
    const targetUser = res.data.user;

    // Build a URL that the new tab will boot from. The token + display info
    // are passed in the query string; the new tab consumes them on mount,
    // stashes them in sessionStorage (NOT localStorage — admin tab isolated)
    // and cleans the URL.
    const url = new URL(window.location.origin);
    url.searchParams.set('impersonation_token', newToken);
    url.searchParams.set('impersonation_name', targetUser.name || '');
    url.searchParams.set('impersonation_email', targetUser.email || '');
    url.searchParams.set('impersonation_admin', user.name || user.email || 'Admin');
    const w = window.open(url.toString(), '_blank', 'noopener,noreferrer');
    if (!w) {
      throw new Error(
        'Browser blocked the new tab. Please allow pop-ups for this site and try again.'
      );
    }
  };

  const returnToAdmin = () => {
    // Used by the impersonation window itself — closes it. The admin's
    // original tab is untouched.
    sessionStorage.removeItem('imp_auth_token');
    sessionStorage.removeItem('imp_active');
    sessionStorage.removeItem('imp_target_name');
    sessionStorage.removeItem('imp_target_email');
    sessionStorage.removeItem('imp_admin_name');
    sessionStorage.removeItem('selected_role');
    try {
      window.close();
    } catch (e) {
      // Some browsers won't let scripts close tabs they didn't open — fall back.
      window.location.href = '/';
    }
  };

  // Check if user has specific role
  const hasRole = (role) => {
    return user && user.roles && user.roles.includes(role);
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return user && user.roles && roles.some(role => user.roles.includes(role));
  };

  // Get applications for current user and role
  const getUserApplications = async () => {
    try {
      const response = await axios.get(`${API}/applications`);
      return response.data;
    } catch (error) {
      console.error('Failed to load applications:', error);
      return [];
    }
  };

  // Get dashboard stats for current user and role
  const getDashboardStats = async () => {
    try {
      let endpoint = '';
      switch (selectedRole) {
        case 'broker':
          endpoint = '/stats/broker';
          break;
        case 'underwriter':
        case 'jr_underwriter':
          endpoint = '/stats/underwriter';
          break;
        case 'admin':
          endpoint = '/stats/admin';
          break;
        default:
          return {};
      }
      
      const response = await axios.get(`${API}${endpoint}`);
      return response.data;
    } catch (error) {
      console.error('Failed to load stats:', error);
      return {};
    }
  };

  // WebSocket connection
  const ws = useWebSocket(token);

  const value = {
    user,
    token,
    selectedRole,
    selectedApplication,
    loading,
    isImpersonationWindow,
    impersonationTarget: isImpersonationWindow ? {
      name: sessionStorage.getItem('imp_target_name') || '',
      email: sessionStorage.getItem('imp_target_email') || '',
      adminName: sessionStorage.getItem('imp_admin_name') || '',
    } : null,
    impersonateUser,
    returnToAdmin,
    login,
    register,
    logout,
    switchRole,
    switchApplication,
    hasRole,
    hasAnyRole,
    getUserApplications,
    getDashboardStats,
    ws,
    isAuthenticated: !!user,
    isRoleSelected: !!selectedRole,
    needsRoleSelection: user && user.roles && user.roles.length > 1 && !selectedRole && !isImpersonationWindow
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};